import React, { useState } from 'react';
import { 
  Mail, 
  Phone, 
  Calendar, 
  User, 
  MoreVertical, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Tag
} from 'lucide-react';

/**
 * LeadCard - Card component for displaying a lead
 * @param {Object} lead - The lead data
 * @param {Object} stage - The current stage configuration
 * @param {Array} allStages - All pipeline stages
 * @param {Function} onMoveStage - Callback to move to different stage
 * @param {Function} onScheduleTour - Callback to schedule tour
 * @param {Function} onSendEmail - Callback to send email
 * @param {Function} onViewDetails - Callback to view full details
 */
const LeadCard = ({ 
  lead, 
  stage, 
  allStages,
  onMoveStage, 
  onScheduleTour, 
  onSendEmail, 
  onViewDetails 
}) => {
  const [showActions, setShowActions] = useState(false);

  // Calculate days since created
  const getDaysSince = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysSince = getDaysSince(lead.created_at);

  // Get initials for avatar
  const getInitials = () => {
    const first = lead.first_name?.[0] || '';
    const last = lead.last_name?.[0] || '';
    return (first + last).toUpperCase();
  };

  // Get stage color for border accent
  const getStageColor = () => {
    const colorMap = {
      'gray': 'border-l-gray-400',
      'blue': 'border-l-blue-400',
      'yellow': 'border-l-yellow-400',
      'purple': 'border-l-purple-400',
      'orange': 'border-l-orange-400',
      'green': 'border-l-green-400',
      'red': 'border-l-red-400'
    };
    return colorMap[stage?.color] || 'border-l-gray-400';
  };

  // Get source badge color
  const getSourceColor = (source) => {
    const colors = {
      'website': 'bg-blue-100 text-blue-700',
      'referral': 'bg-green-100 text-green-700',
      'social_media': 'bg-purple-100 text-purple-700',
      'walk_in': 'bg-orange-100 text-orange-700',
      'phone_inquiry': 'bg-yellow-100 text-yellow-700',
      'email_inquiry': 'bg-gray-100 text-gray-700',
      'advertisement': 'bg-pink-100 text-pink-700',
      'event': 'bg-indigo-100 text-indigo-700',
      'other': 'bg-gray-100 text-gray-600'
    };
    return colors[source] || colors.other;
  };

  // Format source for display
  const formatSource = (source) => {
    if (!source) return 'Unknown';
    return source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get current stage index for navigation
  const currentStageIndex = allStages.findIndex(s => s.name === lead.stage);
  const canMoveLeft = currentStageIndex > 0;
  const canMoveRight = currentStageIndex < allStages.length - 1 && 
                       lead.stage !== 'enrolled' && 
                       lead.stage !== 'lost';

  const handleMoveLeft = (e) => {
    e.stopPropagation();
    if (canMoveLeft) {
      onMoveStage(lead.id, allStages[currentStageIndex - 1].name);
    }
  };

  const handleMoveRight = (e) => {
    e.stopPropagation();
    if (canMoveRight) {
      onMoveStage(lead.id, allStages[currentStageIndex + 1].name);
    }
  };

  const assignedStaff = lead.assigned_to_user;

  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${getStageColor()} 
                  hover:shadow-md transition-all cursor-pointer group relative`}
      onClick={() => onViewDetails(lead)}
    >
      {/* Card Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-purple-700 font-semibold text-sm">
                {getInitials()}
              </span>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                {lead.first_name} {lead.last_name}
              </h3>
              <p className="text-xs text-gray-500 truncate flex items-center">
                <Mail size={10} className="mr-1 flex-shrink-0" />
                {lead.email || 'No email'}
              </p>
            </div>
          </div>
          
          {/* Quick Actions Dropdown */}
          <div className="relative">
            <button 
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 rounded"
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
            >
              <MoreVertical size={18} />
            </button>
            
            {showActions && (
              <>
                <div 
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActions(false);
                  }}
                />
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(lead);
                      setShowActions(false);
                    }}
                  >
                    <User size={14} className="mr-2" />
                    View Details
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onScheduleTour(lead);
                      setShowActions(false);
                    }}
                  >
                    <Calendar size={14} className="mr-2" />
                    Schedule Tour
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSendEmail(lead);
                      setShowActions(false);
                    }}
                  >
                    <Mail size={14} className="mr-2" />
                    Send Email
                  </button>
                  <hr className="my-1" />
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveStage(lead.id, 'lost', 'Marked as lost from menu');
                      setShowActions(false);
                    }}
                  >
                    Mark as Lost
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contact Info */}
        {lead.phone && (
          <div className="mb-2">
            <p className="text-xs text-gray-600 flex items-center">
              <Phone size={12} className="mr-1 text-purple-500" />
              <span>{lead.phone}</span>
            </p>
          </div>
        )}

        {/* Source Badge */}
        <div className="mb-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSourceColor(lead.source)}`}>
            <Tag size={10} className="mr-1" />
            {formatSource(lead.source)}
          </span>
        </div>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center">
            <Clock size={12} className="mr-1" />
            <span className={daysSince > 7 ? 'text-orange-600 font-medium' : ''}>
              {daysSince === 0 ? 'Today' : daysSince === 1 ? '1 day ago' : `${daysSince} days ago`}
            </span>
          </div>
          
          {assignedStaff ? (
            <div className="flex items-center" title={`Assigned to: ${assignedStaff.first_name} ${assignedStaff.last_name}`}>
              <User size={12} className="mr-1 text-purple-500" />
              <span className="truncate max-w-[80px]">
                {assignedStaff.first_name} {assignedStaff.last_name?.[0]}.
              </span>
            </div>
          ) : (
            <span className="text-gray-400 italic">Unassigned</span>
          )}
        </div>
      </div>

      {/* Stage Navigation Footer */}
      <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between bg-gray-50 rounded-b-lg">
        <button
          className={`p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded ${canMoveLeft 
            ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900' 
            : 'text-gray-300 cursor-not-allowed'}`}
          onClick={handleMoveLeft}
          disabled={!canMoveLeft}
          title={canMoveLeft ? `Move to ${allStages[currentStageIndex - 1]?.label}` : ''}
        >
          <ChevronLeft size={18} />
        </button>
        
        <span className="text-xs text-gray-500 font-medium">
          {stage?.label || lead.stage}
        </span>
        
        <button
          className={`p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded ${canMoveRight 
            ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900' 
            : 'text-gray-300 cursor-not-allowed'}`}
          onClick={handleMoveRight}
          disabled={!canMoveRight}
          title={canMoveRight ? `Move to ${allStages[currentStageIndex + 1]?.label}` : ''}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default LeadCard;
